// AE 打包檔（0053）：製作為每個版型上傳一包做好的 AE 檔（壓縮檔），動畫師在被分配到的歌下載。
// 這裡是前後台共用的純判斷（沒有畫面、不碰網路；aePackage.test.ts 有測，後台 cf-worker/aePackages.mjs 也用同一份）：
//   檔案收什麼 / 多大     aePackageFileProblem
//   這包是不是舊的         aePackageStale / aePackageStaleText
//   版型內容的指紋輸入     templateFingerprintInput
//   這首歌要列哪幾包       songPackageRows
//   歌旁邊的 AE 打包檔下載  showSongPackages（製作每首都有；動畫師只有指派給自己的）
//   動畫師頁面的下載       ledDownloadsAllowed（拿掉動畫師那邊的 AE 合成腳本 / 對位圖 / 位置表 / 模板圖）

import { parseWorkScale, workScaleText, workScaleToDb } from './workScale.ts';
import type { ProjectRole } from './mockData.ts';

/** 只收壓縮檔 */
export const AE_PACKAGE_EXTS = ['zip', 'rar', '7z'] as const;
export const AE_PACKAGE_ACCEPT = AE_PACKAGE_EXTS.map(e => '.' + e).join(',');
export const AE_PACKAGE_FILENAME_MAX = 255;

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;
/** 大小上限預設 4 GB（跟影片上傳一樣；wrangler.toml 的 AE_PACKAGE_MAX_BYTES 可以改） */
export const DEFAULT_AE_PACKAGE_MAX_BYTES = 4 * GIB;

/** 後台的上限設定（亂填 / 太小 = 預設，做法跟影片的 VIDEO_MAX_BYTES 一樣） */
export function aePackageMaxBytes(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n >= MIB ? Math.floor(n) : DEFAULT_AE_PACKAGE_MAX_BYTES;
}

export function aePackageExt(filename: string | null | undefined): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(String(filename ?? '').trim());
  return m ? m[1].toLowerCase() : '';
}

export function isAePackageName(filename: string | null | undefined): boolean {
  return (AE_PACKAGE_EXTS as readonly string[]).includes(aePackageExt(filename));
}

function sizeText(n: number): string {
  if (n >= GIB) return `${(n / GIB).toFixed(n >= 10 * GIB ? 0 : 1).replace(/\.0$/, '')} GB`;
  if (n >= MIB) return `${Math.round(n / MIB)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} bytes`;
}
export { sizeText as aePackageSizeText };

/** 檔案能不能收：副檔名 + 大小都檢查。可以 = null，不行 = 一句白話 */
export function aePackageFileProblem(
  file: { name?: string | null; size?: number | null },
  maxBytes: number = DEFAULT_AE_PACKAGE_MAX_BYTES,
): string | null {
  if (!isAePackageName(file.name)) return 'AE 打包檔只收壓縮檔（.zip、.rar、.7z）';
  const size = Number(file.size);
  if (!Number.isInteger(size) || size <= 0) return '這個檔案是空的';
  if (size > maxBytes) return `檔案太大（${sizeText(size)}），上限是 ${sizeText(maxBytes)}`;
  return null;
}

// ───────────────────────── 舊包判斷 ─────────────────────────

export interface AePackageStamp {
  /** 上傳那一刻的製作尺寸（'1/2'；原尺寸 = null） */
  workScale: string | null;
  /** 上傳那一刻版型內容的指紋 */
  fingerprint: string;
}
export interface AePackageStale { scale: boolean; template: boolean; any: boolean }

const scaleKey = (raw: string | null | undefined) => workScaleToDb(parseWorkScale(raw ?? null)) ?? '1/1';

/** 這包是不是舊的：製作尺寸跟現在不同，或版型內容跟現在不同（指紋是空的 = 老資料，只看尺寸） */
export function aePackageStale(pkg: AePackageStamp, now: AePackageStamp): AePackageStale {
  const scale = scaleKey(pkg.workScale) !== scaleKey(now.workScale);
  const template = !!pkg.fingerprint && !!now.fingerprint && pkg.fingerprint !== now.fingerprint;
  return { scale, template, any: scale || template };
}

/** 這包的製作尺寸怎麼稱呼：「1/2 尺寸」「原尺寸」 */
export function aePackageScaleName(raw: string | null | undefined): string {
  const s = parseWorkScale(raw ?? null);
  return s && workScaleToDb(s) ? `${workScaleText(s)} 尺寸` : '原尺寸';
}

/** 舊包提示（動畫師 / 製作都看到）；不是舊的 = null */
export function aePackageStaleText(pkg: Pick<AePackageStamp, 'workScale'>, stale: AePackageStale, who: 'animator' | 'producer'): string | null {
  if (!stale.any) return null;
  const name = aePackageScaleName(pkg.workScale);
  if (who === 'animator') {
    return stale.scale
      ? `這包是舊的（${name}），請跟 PM 確認`
      : `這包是舊的（版型改過，${name}），請跟 PM 確認`;
  }
  if (stale.scale && stale.template) return `這包是舊的：做的時候是${name}，製作尺寸和版型內容都改過了，請重新上傳`;
  return stale.scale
    ? `這包是舊的：做的時候是${name}，現在的製作尺寸不一樣，請重新上傳`
    : '這包是舊的：版型內容改過了，請重新上傳';
}

// ───────────────────────── 版型指紋 ─────────────────────────

interface FingerprintPlan {
  canvas?: { width: number; height: number } | null;
  scalePct?: number;
  cabinet?: { w: number; h: number } | null;
  screens?: readonly {
    key?: string; uv?: unknown; dest: { x: number; y: number; w: number; h: number }; rotate?: number; flipH?: boolean; flipV?: boolean;
    cabinet?: { w: number; h: number } | null;
  }[];
}

/**
 * 版型內容 → 要拿去雜湊的字串（穩定的順序；不含名字、id、時間：改名字不算內容變了）。
 * 「全部」沒有放法，內容就是專案畫布。
 */
export function templateFingerprintInput(plan: FingerprintPlan | null | undefined): string {
  const p = plan ?? {};
  const screens = (p.screens ?? []).map(s => [
    s.key ?? '', s.dest.x, s.dest.y, s.dest.w, s.dest.h, s.rotate ?? 0, s.flipH ? 1 : 0, s.flipV ? 1 : 0,
    s.cabinet ? [s.cabinet.w, s.cabinet.h] : null, s.uv ?? null,
  ]).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  return JSON.stringify({
    c: p.canvas ? [p.canvas.width, p.canvas.height] : null,
    s: p.scalePct ?? 100,
    b: p.cabinet ? [p.cabinet.w, p.cabinet.h] : null,
    p: screens,
  });
}

// ───────────────────────── 這首歌要列哪幾包 ─────────────────────────

export interface SongPackageTemplate { id: string; name: string; kind: 'full' | 'custom' }
export interface SongPackageRow {
  templateId: string;
  templateName: string;
  /** 製作按「全部」那一列 */
  isFull: boolean;
}

/**
 * 這首歌用到哪幾個版型，每個一列（順序：開頭的版型，接著中途換版型的，同一個版型只列一次）。
 * 沒選版型的歌 =「全部」。找不到的版型（剛被刪）略過。
 */
export function songPackageRows(
  templates: readonly SongPackageTemplate[],
  startTemplateId: string | null | undefined,
  switchTemplateIds: readonly string[] | null | undefined,
): SongPackageRow[] {
  const byId = new Map(templates.map(t => [t.id, t]));
  const full = templates.find(t => t.kind === 'full') ?? null;
  const ids: string[] = [];
  const first = startTemplateId && byId.has(startTemplateId) ? startTemplateId : full?.id ?? null;
  if (first) ids.push(first);
  for (const id of switchTemplateIds ?? []) if (byId.has(id) && !ids.includes(id)) ids.push(id);
  return ids.map(id => {
    const t = byId.get(id)!;
    return { templateId: id, templateName: t.name, isFull: t.kind === 'full' };
  });
}

// ───────────────────────── 動畫師頁面不給下載 ─────────────────────────

export type LedDownloadPage = 'studio' | 'editor' | 'director' | 'share';

/**
 * 3D 工具列「⋯」的 AE 合成腳本 / 對位圖 / 位置表（CSV、JSON）/ 版型模板圖和每塊 LED 的圖 zip 要不要給。
 * 動畫師工作站只有製作（直接打開工作站的專案製作）有；動畫師不給（他們改下載製作上傳的 AE 打包檔）。
 * 其他頁面（編輯頁、導演頁、分享頁）這次不動，照原本。
 * 用「頁面 + 這個專案的角色」判斷，不靠隱藏 CSS；「用其他身分看：動畫師」傳進來的角色就是動畫師。
 */
export function ledDownloadsAllowed({ page, role }: { page: LedDownloadPage; role: ProjectRole | null | undefined }): boolean {
  if (page !== 'studio') return true;
  return role === 'admin';
}

// ───────────────────────── 動畫師頁面的下載入口 ─────────────────────────

/**
 * 動畫師工作站要不要在這首歌旁邊顯示「下載 AE 打包檔」：
 *   專案角色是製作（admin）→ 每首歌都顯示（製作本來就能在後台下載，沒被指派也要看得到）
 *   動畫師 → 只有指派給「我」的歌
 *   導演 / 其他 → 沒有
 * 後台權限不變。
 */
export function showSongPackages({ role, userId, song }: {
  role: ProjectRole | null | undefined;
  userId: string | null | undefined;
  song: { animatorUserId?: string | null } | null | undefined;
}): boolean {
  if (!song) return false;
  if (role === 'admin') return true;
  if (!userId || !song.animatorUserId) return false;
  return role === 'animator' && song.animatorUserId === userId;
}
